import notifee, { AndroidImportance } from '@notifee/react-native';
import { Platform } from 'react-native';

import { tr } from '../i18n/runtime';
import { getActiveResolvedVisualThemeId } from '../theme/activeVisualThemeRef';
import { messageSoundAndroidRes, ringSoundAndroidRes } from '../theme/themeSounds';
import { readSavedVisualTheme } from '../theme/visualThemeStorage';
import {
  DEFAULT_VISUAL_THEME,
  isVisualThemeId,
  resolveAutoTheme,
  type ResolvedVisualThemeId,
} from '../theme/visualThemeTypes';
import { readSoundsEnabled } from '../util/soundPrefStorage';

/**
 * Каналы уведомлений Android — одно место на весь проект.
 *
 * <p><b>Зачем модуль.</b> Каналы заводились дважды почти одинаково (в `notifyIncomingMessage`
 * для foreground и в `fcmBackground.android` для headless), и оба раза со звуком `'default'` —
 * то есть системным, а не звуком темы. Получалось две беды сразу: на открытом приложении
 * входящее сообщение звучало <b>дважды</b> (свой звук темы плюс системный от уведомления),
 * а фоновый звонок «звонил» коротким уведомительным «дзынь» вместо рингтона.
 *
 * <p><b>Почему id канала содержит имя звука.</b> Звук канала в Android нельзя перенастроить
 * после создания — это записано и в типах notifee («cannot be overridden once the channel is
 * created»). Пересоздать под тем же id тоже не выйдет: система помнит удалённые каналы и
 * возвращает старые настройки. Поэтому у каждого набора звуков свой id, а лишние соседи
 * удаляются, чтобы в системных настройках телефона висел ровно один канал сообщений и один
 * канал звонков.
 */

/** Ставится в id, чтобы отделиться от каналов старых сборок (`messages` / `calls`). */
const GEN = 'v2';

const LEGACY_IDS = ['messages', 'calls'];

/**
 * Каналы сообщений, оставшиеся от звуков, которых в приложении больше нет.
 *
 * <p>Прополка ниже составляет список по <b>текущим</b> темам, поэтому канал выбывшего звука в
 * него не попадает и живёт в системных настройках телефона вечно: у обновившегося с прежней
 * сборки так и висел бы второй «Сообщения» — от варяжского `message_axe`, заменённого на удар
 * мечей 2026-08-31.
 *
 * <p>Отдельным списком, а не строкой в {@link LEGACY_IDS}: тот отдаётся и прополке каналов
 * звонков, а она удаляла бы канал сообщений — то есть глушила бы уведомления.
 */
const RETIRED_MESSAGE_IDS = [`messages_${GEN}_message_axe`];

/** Все возможные наборы звуков — по ним чистятся каналы неактуальных тем. */
const ALL_THEMES: ResolvedVisualThemeId[] = ['neutral', 'sun', 'dark', 'cosmos', 'varangian'];

/**
 * Тема для канала.
 *
 * <p>Читается с диска, а не из памяти: headless-обработчик FCM живёт в отдельном
 * JS-контексте, где `activeVisualThemeRef` никто не заполнял и он вернул бы `neutral`
 * для любой темы. Значение с диска сырое и может быть `auto` — разрешаем его по часам
 * тем же правилом, что и провайдер темы. Ref остаётся запасным вариантом.
 */
async function channelTheme(): Promise<ResolvedVisualThemeId> {
  try {
    const raw = await readSavedVisualTheme();
    if (isVisualThemeId(raw)) {
      if (raw === 'auto') return resolveAutoTheme(new Date());
      return raw;
    }
  } catch {
    /* падаем на ref ниже */
  }
  try {
    return getActiveResolvedVisualThemeId();
  } catch {
    return DEFAULT_VISUAL_THEME as ResolvedVisualThemeId;
  }
}

function messageChannelIdFor(soundRes: string | null): string {
  return soundRes ? `messages_${GEN}_${soundRes}` : `messages_${GEN}_silent`;
}

function callsChannelIdFor(soundRes: string | null): string {
  return soundRes ? `calls_${GEN}_${soundRes}` : `calls_${GEN}_silent`;
}

/**
 * Удалить перечисленные каналы, кроме актуального.
 *
 * <p>Без этого при каждой смене темы в системных настройках телефона оставался бы ещё один
 * «Сообщения», и через три темы человек видел бы три одинаковых пункта.
 *
 * <p>Список составляет вызывающий, и составляет узко. Беззвучный канал сообщений живёт
 * <b>рядом</b> со звучащим, а не вместо него, поэтому попасть в чужой список прополки он не
 * должен: иначе два канала удаляли бы друг друга по очереди при каждом уведомлении.
 */
async function pruneChannels(keepId: string, candidates: string[]): Promise<void> {
  for (const id of candidates) {
    if (id === keepId) continue;
    try {
      await notifee.deleteChannel(id);
    } catch {
      /* канала могло не быть — это и есть желаемое состояние */
    }
  }
}

let ensured = new Set<string>();

/**
 * Звучащие каналы сообщений всех тем — набор, из которого актуален ровно один, — плюс каналы
 * выбывших звуков: их иначе не убрать, а висят они в системных настройках как лишний дубль.
 */
function allSoundingMessageIds(): string[] {
  return [...ALL_THEMES.map((t) => messageChannelIdFor(messageSoundAndroidRes(t))), ...RETIRED_MESSAGE_IDS];
}

/**
 * Канал входящих сообщений.
 *
 * @param opts.silent канал без звука. Нужен, когда звук играет само приложение (открытое
 *   приложение, сообщение по WS): иначе на одно сообщение слышно два звука — свой,
 *   по теме, и системный от уведомления. Вибрация остаётся: она звук не дублирует.
 *   <p>Именно in-app звук здесь и предпочтительнее: он знает про ограничение частоты
 *   (не чаще раза в две секунды) и про включённое видео, а канал не знает ни о том, ни
 *   о другом — Android играет его на каждое уведомление безусловно.
 */
export async function ensureMessageChannel(opts: { silent?: boolean } = {}): Promise<string> {
  const theme = await channelTheme();
  // Выключенные в настройках звуки — это тоже беззвучный канал: иначе фоновое уведомление
  // звучало бы в обход переключателя, до которого приложение в headless даже не доходит.
  const soundsOn = await readSoundsEnabled();
  const wantSound = !opts.silent && soundsOn;
  const soundRes = wantSound ? messageSoundAndroidRes(theme) : null;
  const id = messageChannelIdFor(soundRes);
  if (!ensured.has(id)) {
    await notifee.createChannel({
      id,
      // Имя у беззвучного своё: два одинаковых «Сообщения» в системных настройках человек
      // различить не сможет, а они делают разное.
      name: wantSound ? tr('notifyChannelMessages') : tr('notifyChannelMessagesSilent'),
      importance: AndroidImportance.HIGH,
      vibration: true,
      ...(soundRes ? { sound: soundRes } : {}),
    });
    ensured.add(id);
    /**
     * Прополка. Звучащий канал убирает звучащие каналы прочих тем, беззвучного не трогая.
     * Беззвучный не убирает ничего — если только звуки не выключены совсем: тогда звучащим
     * каналам сообщений действительно неоткуда взяться.
     */
    const candidates = wantSound
      ? [...allSoundingMessageIds(), ...LEGACY_IDS]
      : soundsOn
        ? [...RETIRED_MESSAGE_IDS, ...LEGACY_IDS]
        : [...allSoundingMessageIds(), ...LEGACY_IDS];
    await pruneChannels(id, candidates);
  }
  return id;
}

/**
 * Канал звонков: рингтон темы вместо системного «дзынь».
 *
 * <p>Зацикливание включается не здесь, а на самом уведомлении (`android.loopSound`) — канал
 * задаёт только файл. Тот, кто рисует полноэкранное уведомление звонка, обязан снять его,
 * когда за звонок берётся in-app оверлей, иначе рингтона будет два.
 */
export async function ensureCallsChannel(): Promise<string> {
  const theme = await channelTheme();
  const soundsOn = await readSoundsEnabled();
  const soundRes = soundsOn ? ringSoundAndroidRes(theme) : null;
  const id = callsChannelIdFor(soundRes);
  if (!ensured.has(id)) {
    await notifee.createChannel({
      id,
      name: tr('notifyChannelCalls'),
      importance: AndroidImportance.HIGH,
      vibration: true,
      ...(soundRes ? { sound: soundRes } : {}),
    });
    ensured.add(id);
    // У звонков вариант всегда один, поэтому прополка забирает и беззвучный тоже.
    await pruneChannels(id, [
      ...ALL_THEMES.map((t) => callsChannelIdFor(ringSoundAndroidRes(t))),
      callsChannelIdFor(null),
      ...LEGACY_IDS,
    ]);
  }
  return id;
}

/**
 * Забыть, какие каналы уже заведены.
 *
 * <p>Вызывается при смене языка (названия каналов локализованы) и при смене темы или
 * переключателя звуков — тогда следующий вызов заведёт канал с новым id и приберёт прежний.
 */
export function invalidateAndroidChannels(): void {
  ensured = new Set<string>();
}

/** Стабильный id полноэкранного уведомления о звонке — чтобы оверлей мог его снять. */
export const INCOMING_CALL_NOTIFICATION_ID = 'incoming-call';

/**
 * Снять полноэкранное уведомление о звонке вместе с его зацикленным рингтоном.
 *
 * <p>Зовётся, когда за звонок берётся in-app оверлей. Уведомление помечено `ongoing` и
 * `autoCancel: false` — само оно не уйдёт, а рингтон у него свой, и без этого вызова
 * телефон звонил бы двумя голосами сразу.
 */
export async function dismissIncomingCallNotification(): Promise<void> {
  if (Platform.OS !== 'android') return;
  try {
    await notifee.cancelNotification(INCOMING_CALL_NOTIFICATION_ID);
  } catch {
    /* уведомления могло не быть — это и есть желаемое состояние */
  }
}
